import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf, Check, ArrowLeft } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const included = [
  "Unlimited journal entries & photos",
  "AI species identification",
  "Voice notes with AI transcription",
  "Interactive maps & GPS tagging",
  "Calendar view & statistics",
  "PDF, JSON & CSV export",
  "Offline mode (PWA)",
  "Lifetime updates",
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // If user already paid, redirect to app
  if (user && user.has_paid) {
    return (
      <div className="min-h-screen bg-leather flex items-center justify-center p-4" data-testid="already-paid">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl border border-[#D2C2B3] p-8 text-center max-w-md shadow-lg">
          <div className="w-16 h-16 rounded-full bg-[#55624F]/10 flex items-center justify-center mx-auto mb-4">
            <Check size={32} weight="bold" className="text-[#55624F]" />
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2C3E2D] mb-2">You're all set!</h2>
          <p className="text-[#7C8068] font-['Manrope'] mb-6">You have lifetime access to Nature Journal.</p>
          <Button
            onClick={() => navigate("/")}
            data-testid="go-to-journal-btn"
            className="bg-[#55624F] hover:bg-[#3d4738] text-white rounded-xl font-['Manrope']"
          >
            Open Journal
          </Button>
        </div>
      </div>
    );
  }

  const handleCheckout = async () => {
    if (!user) {
      navigate("/signup");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${BACKEND_URL}/api/checkout/create-session`,
        { origin_url: origin },
        { withCredentials: false }
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Failed to start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-leather flex items-center justify-center p-4" data-testid="pricing-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-white/70 hover:text-white mb-6 font-['Manrope'] text-sm"
          data-testid="pricing-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-2 border-[#55624F] overflow-hidden shadow-lg">
          <div className="bg-[#55624F] text-white text-center py-3">
            <div className="flex items-center justify-center gap-2">
              <Leaf size={20} weight="duotone" />
              <span className="text-sm font-['Manrope'] font-semibold tracking-wide uppercase">Lifetime Access</span>
            </div>
          </div>
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-[#2C3E2D] font-['Cormorant_Garamond']">$29.99</span>
                <span className="text-[#7C8068] font-['Manrope'] text-sm">one-time</span>
              </div>
              <p className="text-[#7C8068] text-sm font-['Manrope'] mt-2">Pay once, use forever.</p>
            </div>

            <ul className="space-y-3 mb-8">
              {included.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm font-['Manrope'] text-[#2C3E2D]">
                  <Check size={18} weight="bold" className="text-[#55624F] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>

            <Button
              onClick={handleCheckout}
              disabled={loading}
              data-testid="checkout-btn"
              className="w-full bg-[#C25A3C] hover:bg-[#a84830] text-white py-3 rounded-xl font-['Manrope'] font-semibold h-auto text-base"
            >
              {loading ? "Redirecting to checkout..." : "Purchase — $29.99"}
            </Button>

            <p className="text-center text-xs text-[#7C8068] font-['Manrope'] mt-4">
              Secure payment powered by Stripe
            </p>
          </div>
        </div>

        <div className="text-center mt-8">
          <a
            href="https://conniekelts.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors font-['Manrope']"
            data-testid="return-to-site-link"
          >
            <img src={require("../assets/ck-design-avatar.png")} alt="" className="w-8 h-8 rounded-full object-cover border border-white/30 bg-white" />
            &larr; Return to CK Design
          </a>
        </div>
      </div>
    </div>
  );
}
